import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { getFunctions, httpsCallable } from "firebase/functions";
import {
    GoogleAuthProvider,
    signInWithPopup,
    sendSignInLinkToEmail,
    signInWithEmailLink
} from "firebase/auth";
import { auth } from '../../firebase';
import { db } from '../../db';
import analyticsService from '../../services/analytics';
import stravaApi from '../../services/stravaApi';

const provider = new GoogleAuthProvider();

// Load all auth data from IndexedDB
export const loadAllAuth = createAsyncThunk(
    "auth/loadAllAuth",
    async () => {
        const authData = await db.auth.get(1);
        return {
            rwgps: authData?.rwgps || null,
            strava: authData?.strava || null
        };
    }
);

// Legacy functions for backward compatibility
export const loadRWGPSAuth = createAsyncThunk(
    "auth/loadRWGPSAuth",
    async () => {
        const authData = await db.auth.get(1);
        return authData?.rwgps || null;
    }
);

export const loadStravaAuth = createAsyncThunk(
    "auth/loadStravaAuth",
    async () => {
        const authData = await db.auth.get(1);
        return authData?.strava || null;
    }
);

export const getToken = createAsyncThunk(
    "auth/getToken",
    async ({ code }) => {
        // exchange the oauth code for an oauth token
        //console.log('get Token')
        const functions = getFunctions();
        const getToken = httpsCallable(functions, 'rwgps_get_access_token');

        const result = await getToken({ code });
        const tokenData = JSON.parse(result.data);

        // Store the token in IndexedDB, preserving existing strava auth
        const existingAuth = await db.auth.get(1);
        await db.auth.put({
            id: 1,
            rwgps: tokenData,
            strava: existingAuth?.strava || null
        });

        return tokenData;
    })

export const getStravaToken = createAsyncThunk(
    "auth/getStravaToken",
    async ({ code }) => {
        // exchange the oauth code for an oauth token
        const functions = getFunctions();

        const getToken = httpsCallable(functions, 'strava_get_access_token');

        const result = await getToken({ code });
        const tokenData = JSON.parse(result.data);

        // Store the token in IndexedDB, preserving existing rwgps auth
        const existingAuth = await db.auth.get(1);
        await db.auth.put({
            id: 1,
            strava: tokenData,
            rwgps: existingAuth?.rwgps || null
        });

        return tokenData;
    })

export const revokeToken = createAsyncThunk(
    "auth/revokeToken",
    async (_, { getState }) => {
        // revoke the oauth token
        const functions = getFunctions();

        const revokeToken = httpsCallable(functions, 'rwgps_revoke_access_token');

        // Get the current RWGPS token from state
        const state = getState();
        const accessToken = state.auth.connections.rwgps?.access_token;

        if (!accessToken) {
            throw new Error('No access token to revoke');
        }

        await revokeToken({ access_token: accessToken });

        // Clear the token from IndexedDB, preserving existing strava auth
        const existingAuth = await db.auth.get(1);
        await db.auth.put({
            id: 1,
            rwgps: null,
            strava: existingAuth?.strava || null
        });
    })

export const revokeStravaToken = createAsyncThunk(
    "auth/revokeStravaToken",
    async (_, { getState }) => {
        // revoke the oauth token
        const functions = getFunctions();

        const revokeToken = httpsCallable(functions, 'strava_revoke_access_token');

        // Get the current Strava token from state
        const state = getState();
        const accessToken = state.auth.connections.strava?.access_token;

        if (!accessToken) {
            throw new Error('No access token to revoke');
        }

        await revokeToken({ access_token: accessToken });

        // Clear the token from IndexedDB, preserving existing rwgps auth
        const existingAuth = await db.auth.get(1);
        await db.auth.put({
            id: 1,
            strava: null,
            rwgps: existingAuth?.rwgps || null
        });
    })

export const refreshStravaToken = createAsyncThunk(
    "auth/refreshStravaToken",
    async (_, { getState, dispatch }) => {
        const state = getState();
        const stravaAuth = state.auth.connections.strava;

        if (!stravaAuth || !stravaAuth.refresh_token) {
            throw new Error('No refresh token available');
        }

        // Check if token needs refresh
        if (!stravaApi.isTokenExpired(stravaAuth)) {
            return stravaAuth; // Token is still valid
        }

        try {
            // Refresh the token using the frontend API service
            const newTokenData = await stravaApi.refreshAccessToken(stravaAuth.refresh_token);

            // Store the new token in IndexedDB, preserving existing rwgps auth
            const existingAuth = await db.auth.get(1);
            await db.auth.put({
                id: 1,
                strava: newTokenData,
                rwgps: existingAuth?.rwgps || null
            });

            return newTokenData;
        } catch (error) {
            console.error('Failed to refresh Strava token:', error);
            throw error;
        }
    }
)

// Firebase Auth Methods
export const loginWithGoogle = createAsyncThunk(
    "auth/loginWithGoogle",
    async (_, { rejectWithValue }) => {
        try {
            auth.useDeviceLanguage();
            const result = await signInWithPopup(auth, provider);
            const userData = _.pick(result.user, ["displayName", "email", "photoURL", "uid"]);

            // Track login event
            analyticsService.trackAuthEvent('login', 'google');
            analyticsService.setUserId(userData.uid);

            return userData;
        } catch (error) {
            // Track login failure
            analyticsService.trackAuthEvent('login_failed', 'google');
            return rejectWithValue(error.message);
        }
    }
);

export const logoutUser = createAsyncThunk(
    "auth/logoutUser",
    async (_, { dispatch }) => {
        // Track logout event
        analyticsService.trackAuthEvent('logout');

        await auth.signOut();
        dispatch(logout()); // Clear Redux state
    }
);

export const sendEmailLoginLink = createAsyncThunk(
    "auth/sendEmailLoginLink",
    async (email, { rejectWithValue }) => {
        try {
            const actionCodeSettings = {
                url: window.location.origin,
                handleCodeInApp: true
            };
            await sendSignInLinkToEmail(auth, email, actionCodeSettings);
            window.localStorage.setItem('emailForSignIn', email);

            // Track email login link sent
            analyticsService.trackAuthEvent('email_link_sent');

            return { success: true };
        } catch (error) {
            // Track email login failure
            analyticsService.trackAuthEvent('email_link_failed');
            return rejectWithValue(error.message);
        }
    }
);

export const completeEmailSignIn = createAsyncThunk(
    "auth/completeEmailSignIn",
    async (_, { rejectWithValue }) => {
        try {
            let email = window.localStorage.getItem('emailForSignIn');
            if (!email) {
                email = window.prompt('Please provide your email for confirmation');
            }
            const result = await signInWithEmailLink(auth, email, window.location.href);
            window.localStorage.removeItem('emailForSignIn');

            // Track successful email sign-in
            analyticsService.trackAuthEvent('login', 'email');
            if (result.user) {
                analyticsService.setUserId(result.user.uid);
            }

            return { success: true };
        } catch (error) {
            // Track email sign-in failure
            analyticsService.trackAuthEvent('email_signin_failed');
            return rejectWithValue(error.message);
        }
    }
);

export const authSlice = createSlice({
    name: 'auth',
    initialState: {
        user: null,
        connections: {
            rwgps: null,
            strava: null,
        },
        isLoading: true,
        connectionsLoaded: false,
    },
    reducers: {
        setUser(state, action) {
            state.user = action.payload;
            state.isLoading = false;

            // Track user session start
            if (action.payload) {
                analyticsService.setUserId(action.payload.uid);
            }
        },
        logout(state) {
            state.user = null;
            state.connections = { rwgps: null, strava: null };
            state.isLoading = false;
            state.connectionsLoaded = false;
        },
        clearRWGPSAuth(state) {
            state.connections.rwgps = null;
            // Clear from IndexedDB as well, preserving existing strava auth
            db.auth.get(1).then(existingAuth => {
                db.auth.put({
                    id: 1,
                    rwgps: null,
                    strava: existingAuth?.strava || null
                });
            });
        },
        clearStravaAuth(state) {
            state.connections.strava = null;
            // Clear from IndexedDB as well, preserving existing rwgps auth
            db.auth.get(1).then(existingAuth => {
                db.auth.put({
                    id: 1,
                    strava: null,
                    rwgps: existingAuth?.rwgps || null
                });
            });
        },
        setConnectionsLoaded(state, action) {
            state.connectionsLoaded = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getToken.fulfilled, (state, action) => {
                state.connections.rwgps = action.payload;
            })
            .addCase(loadRWGPSAuth.fulfilled, (state, action) => {
                state.connections.rwgps = action.payload;
                state.connectionsLoaded = true;
            })
            .addCase(loadStravaAuth.fulfilled, (state, action) => {
                state.connections.strava = action.payload;
                state.connectionsLoaded = true;
            })
            .addCase(loadAllAuth.fulfilled, (state, action) => {
                state.connections.rwgps = action.payload.rwgps;
                state.connections.strava = action.payload.strava;
                state.connectionsLoaded = true;
            })
            .addCase(getStravaToken.fulfilled, (state, action) => {
                state.connections.strava = action.payload;
            })
            .addCase(revokeToken.fulfilled, (state) => {
                state.connections.rwgps = null;
            })
            .addCase(revokeStravaToken.fulfilled, (state) => {
                state.connections.strava = null;
            })
            .addCase(refreshStravaToken.fulfilled, (state, action) => {
                state.connections.strava = action.payload;
            })
            .addCase(refreshStravaToken.rejected, (state) => {
                state.connections.strava = null;
            })
            .addCase(loginWithGoogle.fulfilled, (state, action) => {
                state.user = action.payload;
                state.isLoading = false;
            })
            .addCase(loginWithGoogle.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(logoutUser.fulfilled, (state) => {
                state.user = null;
                state.connections = { rwgps: null, strava: null };
                state.isLoading = false;
                state.connectionsLoaded = false;
            })
            .addCase(sendEmailLoginLink.fulfilled, (state) => {
                // Email link sent successfully
            })
            .addCase(sendEmailLoginLink.rejected, (state) => {
                // Handle error if needed
            })
            .addCase(completeEmailSignIn.fulfilled, (state) => {
                // Email sign-in completed successfully
            })
            .addCase(completeEmailSignIn.rejected, (state) => {
                // Handle error if needed
            });
    }
})

export const {
    setUser,
    logout,
    clearRWGPSAuth,
    clearStravaAuth,
    setConnectionsLoaded,
} = authSlice.actions

export default authSlice.reducer